(() => {
  'use strict';
  const TAU = Math.PI * 2;
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const mix = (a, b, t) => a + (b - a) * t;
  const ease = t => t * t * (3 - 2 * t);
  const videoPosters = new WeakMap();
  const palette = { steel: '#c7d2da', dark: '#38576b', blue: '#185f92', amber: '#e2a04b', pack: '#e2c49b', white: '#f1eee7', green: '#74a897', red: '#bb6252', light: '#dfe9ef' };
  const hex = s => [parseInt(s.slice(1, 3), 16), parseInt(s.slice(3, 5), 16), parseInt(s.slice(5, 7), 16)];
  const shade = (c, k) => 'rgb(' + hex(c).map(n => Math.round(clamp(n * k, 0, 255))).join(',') + ')';

  class ProcessView {
    constructor(root) {
      this.root = root;
      this.canvas = root.querySelector('canvas');
      if (!this.canvas) return;
      this.ctx = this.canvas.getContext('2d');
      if (!this.ctx) return;
      this.family = root.dataset.cxvFamily || 'bag';
      this.variant = root.dataset.cxvVariant || '';
      this.steps = [...root.querySelectorAll('[data-cxv-step]')];
      this.step = 0;
      this.progress = 0.45;
      this.playing = false;
      this.visible = false;
      this.yaw = -0.52;
      this.pitch = 0.46;
      this.zoom = 1;
      this.polys = [];
      this.strokes = [];
      this.raf = 0;
      this.last = 0;
      this.lastPaint = 0;
      this.elapsed = 0;
      this.frames = 0;
      this.speed = 1;
      this.play = root.querySelector('[data-cxv-play]');
      this.bind();
      this.resize();
      this.select(0, false);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (this.visible) { this.last = 0; this.draw(); this.schedule(); }
        else { cancelAnimationFrame(this.raf); this.raf = 0; }
      }, { rootMargin: '120px' });
      this.observer.observe(this.canvas);
      this.resizer = new ResizeObserver(() => this.resize());
      this.resizer.observe(this.canvas);
    }
    bind() {
      this.steps.forEach((button, i) => button.addEventListener('click', () => {
        this.setPlaying(false);
        this.select(i, true);
      }));
      this.play.addEventListener('click', () => this.setPlaying(!this.playing));
      this.root.querySelectorAll('[data-cxv-camera]').forEach(button => button.addEventListener('click', () => {
        const action = button.dataset.cxvCamera;
        if (action === 'left') this.yaw -= 0.24;
        if (action === 'right') this.yaw += 0.24;
        if (action === 'in') this.zoom = clamp(this.zoom + 0.13, 0.65, 1.55);
        if (action === 'out') this.zoom = clamp(this.zoom - 0.13, 0.65, 1.55);
        if (action === 'reset') { this.yaw = -0.52; this.pitch = 0.46; this.zoom = 1; }
        this.draw();
      }));
      const speed = this.root.querySelector('[data-cxv-speed]');
      if (speed) speed.addEventListener('change', () => { this.speed = Number(speed.value) || 1; });
      let drag = null;
      this.canvas.addEventListener('pointerdown', e => {
        drag = { x: e.clientX, y: e.clientY, yaw: this.yaw, pitch: this.pitch, touch: e.pointerType === 'touch' };
        this.canvas.setPointerCapture(e.pointerId);
        this.canvas.focus({ preventScroll: true });
      });
      this.canvas.addEventListener('pointermove', e => {
        if (!drag) return;
        this.yaw = drag.yaw + (e.clientX - drag.x) * 0.008;
        if (!drag.touch) this.pitch = clamp(drag.pitch + (e.clientY - drag.y) * 0.005, 0.17, 0.92);
        this.draw();
      });
      this.canvas.addEventListener('pointerup', () => { drag = null; });
      this.canvas.addEventListener('pointercancel', () => { drag = null; });
      this.canvas.addEventListener('keydown', e => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', '+', '-', 'Home'].includes(e.key)) return;
        e.preventDefault();
        if (e.key === 'ArrowLeft') this.yaw -= 0.16;
        if (e.key === 'ArrowRight') this.yaw += 0.16;
        if (e.key === 'ArrowUp') this.pitch = clamp(this.pitch + 0.08, 0.17, 0.92);
        if (e.key === 'ArrowDown') this.pitch = clamp(this.pitch - 0.08, 0.17, 0.92);
        if (e.key === '+') this.zoom = clamp(this.zoom + 0.1, 0.65, 1.55);
        if (e.key === '-') this.zoom = clamp(this.zoom - 0.1, 0.65, 1.55);
        if (e.key === 'Home') { this.yaw = -0.52; this.pitch = 0.46; this.zoom = 1; }
        this.draw();
      });
      document.addEventListener('visibilitychange', () => {
        this.last = 0;
        if (document.hidden) { cancelAnimationFrame(this.raf); this.raf = 0; }
        else this.schedule();
      });
      this.root.addEventListener('cxv:visible', () => { this.resize(); this.draw(); });
    }
    select(index, manual) {
      this.step = index;
      this.elapsed = 0;
      this.progress = manual ? 0.58 : 0.45;
      this.steps.forEach((button, i) => {
        button.setAttribute('aria-pressed', String(i === index));
        button.classList.toggle('is-active', i === index);
      });
      const selected = this.steps[index];
      this.root.querySelector('[data-cxv-current]').textContent = selected.dataset.cxvTitle;
      this.root.querySelector('[data-cxv-current-role]').textContent = selected.dataset.cxvRole;
      this.root.querySelector('[data-cxv-counter]').textContent = '0' + (index + 1) + ' / 04';
      this.root.querySelector('[data-cxv-progress]').style.width = ((index + this.progress) * 25) + '%';
      this.canvas.dataset.step = String(index + 1);
      this.draw();
    }
    setPlaying(value) {
      this.playing = value;
      this.play.setAttribute('aria-pressed', String(value));
      this.play.querySelector('[data-cxv-play-label]').textContent = value ? this.play.dataset.pauseLabel : this.play.dataset.playLabel;
      this.play.querySelector('[data-cxv-play-icon]').textContent = value ? 'Ⅱ' : '▶';
      this.last = 0;
      if (value) { this.elapsed = 0; this.schedule(); }
      else { cancelAnimationFrame(this.raf); this.raf = 0; }
    }
    schedule() {
      if (!this.playing || !this.visible || document.hidden || this.raf) return;
      this.raf = requestAnimationFrame(t => this.tick(t));
    }
    tick(now) {
      this.raf = 0;
      if (!this.playing || !this.visible || document.hidden) return;
      const delta = this.last ? Math.min(now - this.last, 100) : 0;
      this.last = now;
      this.elapsed += delta * this.speed;
      if (this.elapsed >= 3400) this.select((this.step + 1) % 4, false);
      this.progress = clamp(this.elapsed / 3400, 0, 1);
      if (now - this.lastPaint >= 32) {
        this.draw();
        this.lastPaint = now;
      }
      this.root.querySelector('[data-cxv-progress]').style.width = ((this.step + this.progress) * 25) + '%';
      this.schedule();
    }
    resize() {
      const rect = this.canvas.getBoundingClientRect();
      if (rect.width < 2 || rect.height < 2) return;
      this.width = rect.width;
      this.height = rect.height;
      const ratio = Math.min(window.devicePixelRatio || 1, 1.75);
      this.canvas.width = Math.round(rect.width * ratio);
      this.canvas.height = Math.round(rect.height * ratio);
      this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      this.draw();
    }
    project(v) {
      const x = v[0] * Math.cos(this.yaw) - v[2] * Math.sin(this.yaw);
      const z = v[0] * Math.sin(this.yaw) + v[2] * Math.cos(this.yaw);
      const y = v[1] - 1.35;
      const up = y * Math.cos(this.pitch) - z * Math.sin(this.pitch);
      const depth = y * Math.sin(this.pitch) + z * Math.cos(this.pitch);
      const perspective = 22 / (22 - depth);
      const scale = Math.min(this.width / 10.5, this.height / 6.1) * this.zoom;
      return [this.width / 2 + x * scale * perspective, this.height * 0.52 - up * scale * perspective, depth];
    }
    poly(points, color, light = 1, alpha = 1) {
      const projected = points.map(v => this.project(v));
      this.polys.push({ points: projected, depth: projected.reduce((n, v) => n + v[2], 0) / points.length, color: shade(color, light), alpha });
    }
    box(x, y, z, w, h, d, color, alpha = 1) {
      const p = [[x-w/2,y,z-d/2],[x+w/2,y,z-d/2],[x+w/2,y,z+d/2],[x-w/2,y,z+d/2],[x-w/2,y+h,z-d/2],[x+w/2,y+h,z-d/2],[x+w/2,y+h,z+d/2],[x-w/2,y+h,z+d/2]];
      [[0,1,2,3],[0,4,5,1],[1,5,6,2],[2,6,7,3],[3,7,4,0],[4,7,6,5]].forEach((face,i) => this.poly(face.map(j=>p[j]), color, [0.68,0.78,0.9,0.72,0.82,1.08][i], alpha));
    }
    tube(a, b, r, color, sides = 10, endRadius = r) {
      const v = [b[0]-a[0],b[1]-a[1],b[2]-a[2]];
      const len = Math.hypot(...v) || 1;
      const n = v.map(x=>x/len);
      const ref = Math.abs(n[1]) < 0.9 ? [0,1,0] : [1,0,0];
      let u = [n[1]*ref[2]-n[2]*ref[1],n[2]*ref[0]-n[0]*ref[2],n[0]*ref[1]-n[1]*ref[0]];
      const ul = Math.hypot(...u); u=u.map(x=>x/ul);
      const vv = [n[1]*u[2]-n[2]*u[1],n[2]*u[0]-n[0]*u[2],n[0]*u[1]-n[1]*u[0]];
      const lower=[], upper=[];
      for(let i=0;i<sides;i++) {
        const c=Math.cos(TAU*i/sides),s=Math.sin(TAU*i/sides);
        lower.push(a.map((q,j)=>q+(u[j]*c+vv[j]*s)*r));
        upper.push(b.map((q,j)=>q+(u[j]*c+vv[j]*s)*endRadius));
      }
      for(let i=0;i<sides;i++) this.poly([lower[i],lower[(i+1)%sides],upper[(i+1)%sides],upper[i]],color,0.75+0.24*Math.cos(TAU*i/sides-0.8));
      this.poly(upper,color,1.08);
      this.poly(lower,color,0.72);
    }
    conveyor(x,z,length=4,height=0.7,width=0.85) {
      this.box(x,height,z,length,0.16,width,palette.dark);
      this.box(x,height+0.17,z,length,0.025,width-0.08,'#6c858f');
      for(const end of [-1,1])for(const side of [-1,1])this.box(x+end*(length/2-0.2),0,z+side*(width/2-0.08),0.1,height,0.1,palette.steel);
      for(let i=-length/2+0.2;i<length/2;i+=0.48)this.box(x+i,height+0.20,z,0.025,0.01,width-0.1,'#a4b6bf');
    }
    hopper(x,y,z,r=0.5,h=0.75) {
      this.tube([x,y,z],[x,y+h,z],0.12,palette.steel,12,r);
      this.tube([x,y+h,z],[x,y+h+0.35,z],r,palette.steel,12,r);
      this.box(x+r+0.18,y+0.08,z,0.22,0.4,0.28,palette.blue);
    }
    pack(x,y,z,filled=1,kind='bag',scale=1) {
      const w=0.58*scale,h=(0.38+filled*0.38)*scale,d=0.34*scale;
      const color=kind==='case'?palette.pack:kind==='film'?palette.green:palette.white;
      this.box(x,y,z,w,h,d,color);
      this.box(x,y+h*0.34,z+d/2+0.004,w*0.68,0.14*scale,0.012,palette.blue);
      if(filled>0.7)this.box(x,y+h,z,w*0.95,0.035*scale,d*0.9,palette.amber);
      else this.box(x,y+h,z,w*0.88,0.012,d*0.8,palette.dark);
    }
    particles(x,y,z,length=0.8,color=palette.amber) {
      for(let i=0;i<13;i++) {
        const drop=(this.progress*2.6+i/13)%1;
        this.box(x+Math.sin(i*7)*0.065,y-drop*length,z+Math.cos(i*5)*0.05,0.035,0.055,0.035,color);
      }
    }
    frame(x,z,w=1.45,h=2.3,d=1.25) {
      for(const xx of [-1,1])for(const zz of [-1,1])this.box(x+xx*w/2,0,z+zz*d/2,0.075,h,0.075,palette.steel);
      this.box(x,h,z,w+0.07,0.08,d+0.07,palette.steel);
      this.box(x+w/2+0.12,1.2,z+d/2,0.25,0.55,0.12,palette.blue);
    }
    pallet(x,z,layers=2,kind='bag') {
      for(let i=-1;i<=1;i++)this.box(x,0.04,z+i*0.45,1.9,0.1,0.22,palette.pack);
      for(let i=-2;i<=2;i++)this.box(x+i*0.35,0.15,z,0.18,0.08,1.5,palette.pack);
      for(let l=0;l<layers;l++)for(let i=0;i<3;i++)this.pack(x-0.55+i*0.55,0.24+l*0.45,z+(l%2?0.18:-0.08),1,kind,0.8);
    }
    paperScene(manual=false) {
      this.conveyor(0.75,0,6.6,0.55,1);
      if(manual&&this.variant==='twin') {
        this.hopper(-2.7,2.1,-0.25,0.5,0.55);
        for(const offset of [-0.18,0.18])this.tube([-2.7,2.1,-0.25+offset],[-0.05,2.1,-0.25+offset],0.1,palette.steel);
      }else{
        this.hopper(-2.7,1.05,-0.25,0.5,0.7);
        this.tube([-2.7,1.1,-0.25],[-0.05,2.7,-0.25],0.11,palette.steel);
      }
      const twin=this.variant==='dual';
      const lanes=twin?[-0.48,0.48]:[0];
      this.frame(0,0,twin?2.25:1.5,2.55,1.5);
      lanes.forEach((z,i) => {
        const x=twin?(i?0.5:-0.5):0;
        this.hopper(x,1.85,z,0.37,0.48);
        const filled=this.step<2?0.1:this.step===2?this.progress:1;
        this.pack(x,0.76,z,filled,'bag',1.05);
        if(this.step===2)this.particles(x,1.8,z,0.55);
      });
      for(let i=0;i<5;i++)this.box(-1.7,0.78+i*0.025,0.65,0.5,0.022,0.5,palette.pack);
      if(!manual) {
        const pick=this.step===1?ease(this.progress):0.1;
        this.tube([-1.55,1.75,0.45],[-1.55+pick*1.2,1.75,0.1],0.055,palette.dark);
        this.box(-1.55+pick*1.2,1.58,0.1,0.25,0.08,0.28,palette.blue);
      } else {
        this.tube([-1.4,1.6,1.1],[-1.4,1.85,1.1],0.14,palette.amber);
        this.box(-1.4,0.8,1.1,0.34,0.75,0.22,palette.blue);
        this.tube([-1.3,1.4,1.1],[-0.7,1.15,0.3],0.06,palette.amber);
      }
      this.frame(1.7,0,0.8,1.8,1.1);
      const jaw=this.step===3?0.2+0.1*Math.sin(this.progress*Math.PI):0.42;
      this.box(1.7,1.48,-jaw,0.48,0.12,0.15,palette.blue);
      this.box(1.7,1.48,jaw,0.48,0.12,0.15,palette.blue);
      this.pack(this.step===3?mix(0.5,2.8,ease(this.progress)):2.7,0.76,0,1,'bag');
    }
    filmScene(liquid=false) {
      this.conveyor(1.7,0,3.2,0.45,0.9);
      this.frame(0,0,1.7,2.65,1.6);
      if(liquid) {
        this.tube([-2.4,0.2,0],[-2.4,1.9,0],0.57,palette.steel,14);
        this.tube([-2.4,1.9,0],[0,2.5,0],0.09,palette.blue);
      }else if(this.variant==='granules'||this.variant==='heavy') {
        this.hopper(0,2.5,0,0.45,0.4);
        for(let i=0;i<6;i++)this.hopper(Math.cos(TAU*i/6)*.53,2.05,Math.sin(TAU*i/6)*.53,.18,.33);
      }else this.hopper(0,2.2,0,0.52,0.55);
      this.tube([-1.2,1.9,-0.4],[-1.2,1.9,0.4],0.43,palette.green,16);
      this.poly([[-1.2,2.3,0.4],[-1.2,2.3,-0.4],[0.2,2.1,-0.4],[0.2,2.1,0.4]],palette.green,1,0.7);
      this.tube([0,1.15,0],[0,2.2,0],0.23,palette.steel,12);
      this.pack(0,0.63,0,this.step>=2?this.progress:0.18,'film',1.2);
      const pinch=this.step===3?0.15:0.38;
      this.box(0,0.8,-pinch,0.8,0.12,0.15,palette.blue);
      this.box(0,0.8,pinch,0.8,0.12,0.15,palette.blue);
      if(this.step===2)this.particles(0,1.75,0,0.8,liquid?'#5e9bac':palette.amber);
      this.pack(2.5,0.68,0,1,'film');
    }
    rotaryScene(large=false) {
      this.tube([0,0.2,0],[0,0.55,0],1.25,palette.blue,16);
      const turn=this.step===1?this.progress*0.7:0;
      const count=large?8:6;
      for(let i=0;i<count;i++) {
        const a=i*TAU/count+turn;
        this.pack(Math.cos(a),0.65,Math.sin(a),i/(count-1),large?'bag':'film',large?1:0.8);
      }
      this.hopper(0,1.8,-0.9,0.46,0.6);
      this.frame(0,-0.9,0.8,2.4,0.8);
      if(this.step===2)this.particles(0,1.8,-0.9,0.6);
      this.box(1.2,1.2,0,0.2,0.2,0.65,palette.dark);
      this.conveyor(2.45,0,2,0.5,0.8);
      this.pack(2.7,0.72,0,1,'film');
      for(let i=0;i<5;i++)this.box(-2,0.6+i*0.025,0,0.6,0.022,0.42,palette.green);
    }
    cartonScene(container=false) {
      this.conveyor(0,0,6.6,0.58,1);
      this.frame(0,0,1.5,2.5,1.3);
      this.hopper(0,1.9,0,0.48,0.7);
      if(container) {
        for(let i=0;i<5;i++) {
          const x=-2.5+i*1.15;
          this.tube([x,0.78,0],[x,1.42,0],0.22,palette.white,12);
          this.box(x,1.02,0.225,0.3,0.19,0.01,palette.blue);
          if(i>2)this.tube([x,1.42,0],[x,1.48,0],0.23,palette.blue,12);
        }
        if(this.step===1)this.particles(0,1.9,0,0.45);
      } else {
        for(let i=0;i<6;i++)this.box(-2.5,0.78+i*0.035,0,0.6,0.03,0.58,palette.pack);
        const lid=this.step<2?0.62:0.24;
        this.openCase(0,0.78,0,0.8,lid);
        if(this.step===1)this.particles(0,1.9,0,0.7);
        this.pack(2.6,0.78,0,1,'case',1.2);
      }
    }
    openCase(x,y,z,s=1,open=0.5) {
      this.box(x,y,z,s,0.08,s*0.8,palette.pack);
      this.box(x-s/2,y,z,0.055,s*0.7,s*0.8,palette.pack);
      this.box(x+s/2,y,z,0.055,s*0.7,s*0.8,palette.pack);
      this.box(x,y,z-s*0.4,s,0.7*s,0.05,palette.pack);
      this.box(x,y,z+s*0.4,s,0.7*s,0.05,palette.pack);
      this.poly([[x-s/2,y+s*.7,z-s*.4],[x+s/2,y+s*.7,z-s*.4],[x+s/2,y+s*.7+open,z-s*.7],[x-s/2,y+s*.7+open,z-s*.7]],palette.pack);
      this.poly([[x-s/2,y+s*.7,z+s*.4],[x+s/2,y+s*.7,z+s*.4],[x+s/2,y+s*.7+open,z+s*.7],[x-s/2,y+s*.7+open,z+s*.7]],palette.pack);
    }
    caseScene() {
      this.conveyor(0,0,6.5,0.6,1.35);
      const v=this.variant;
      if(v==='erector') {
        for(let i=0;i<5;i++)this.box(-2.4,0.82+i*0.04,0,0.9,0.035,0.75,palette.pack);
        this.openCase(0,0.82,0,1.1,this.step<2?0.5:0.1);
      }else if(v==='packer') {
        this.openCase(0,0.82,0,1.3,0.45);
        for(let i=0;i<4;i++)this.pack(-2.7+i*0.52,0.82,0,1,'film',0.65);
        if(this.step>=2)for(let i=0;i<3;i++)this.pack(-0.4+i*.4,0.92,0,1,'film',0.58);
        this.frame(0,0,1.7,2.5,1.7);
        this.tube([0,2.4,0],[0,1.8,0],0.1,palette.blue);
      }else if(v==='strap') {
        this.pack(0,0.82,0,1,'case',1.6);
        this.frame(0,0,1.9,2.35,1.8);
        if(this.step>=2){this.box(0,0.85,0.42,0.06,1.16,0.025,palette.blue);this.box(0,2,0,0.06,0.025,0.85,palette.blue);}
      }else {
        this.pack(0,0.82,0,1,'case',1.55);
        this.frame(0,0,1.8,2.1,1.6);
        this.tube([0,2.02,-0.13],[0,2.02,0.13],0.27,palette.pack);
        this.box(0.45,1.99,0,1,0.025,0.08,palette.amber);
      }
      this.pack(2.5,0.82,0,1,'case',1.15);
    }
    secondaryScene() {
      this.conveyor(-1,0,4.3,0.6,1);
      for(let i=0;i<5;i++)this.pack(-2.5+i*0.55,0.82,0,1,'film',0.62);
      this.frame(1,0,1.55,2.25,1.3);
      if(this.variant==='woven') {
        this.pack(1,0.82,0,this.step>1?0.8:0.2,'bag',1.65);
        if(this.step===2)this.pack(1,1.8-this.progress*.6,0,1,'film',0.6);
      }else {
        for(let i=0;i<3;i++)this.pack(0.5+i*0.48,0.82,0,1,'film',0.65);
        if(this.step>=2)this.box(1,0.84,0,1.5,0.62,0.65,palette.green,0.35);
      }
      this.conveyor(2.7,0,1.5,0.6,1.25);
    }
    robotScene(high=false) {
      const kind=this.variant==='case'?'case':'bag';
      this.conveyor(-2,0,2.6,high?1.9:0.65,0.9);
      this.pack(-2.2,high?2.12:0.87,0,1,kind);
      this.pallet(1.75,0,this.step>=2?2:1,kind);
      if(high) {
        this.frame(0.6,0,3.4,2.5,2.4);
        this.box(0.4,2.1,0,2.8,0.15,1.5,palette.blue);
        for(let i=0;i<3;i++)this.pack(-0.3+i*0.65,2.27,0,1,'bag',0.85);
        if(this.step===2)this.box(1.6,1.5-this.progress,0,1.9,0.15,1.45,palette.dark);
      }else {
        const base=[-0.25,0.75,-1.15];
        this.tube([-0.25,0,-1.15],base,0.44,palette.dark,12);
        const t=this.step===1?ease(this.progress):this.step===2?1:0;
        const target=[mix(-2,1.5,t),this.step===2?1.5:1.15,mix(0,0.05,t)];
        const dx=target[0]-base[0],dz=target[2]-base[2],dh=Math.hypot(dx,dz),dy=target[1]-base[1];
        const dist=clamp(Math.hypot(dh,dy),0.2,2.9),a=1.6,b=1.4;
        const angle=Math.atan2(dy,dh)+Math.acos(clamp((a*a+dist*dist-b*b)/(2*a*dist),-1,1));
        const elbow=[base[0]+dx/dh*a*Math.cos(angle),base[1]+a*Math.sin(angle),base[2]+dz/dh*a*Math.cos(angle)];
        this.tube(base,elbow,0.17,palette.amber,10);
        this.tube(elbow,target,0.14,palette.amber,10);
        this.tube([elbow[0],elbow[1],elbow[2]-0.15],[elbow[0],elbow[1],elbow[2]+0.15],0.22,palette.dark);
        this.box(target[0],target[1]-.12,target[2],0.65,0.12,0.5,palette.dark);
        if(this.step===1||this.step===2)this.pack(target[0],target[1]-.7,target[2],1,kind,0.85);
      }
    }
    weigherScene() {
      this.frame(0,0,2.6,2.2,2.3);
      this.hopper(0,2.1,0,0.75,0.6);
      const multi=this.variant==='multihead';
      const count=multi?8:this.variant==='twin'?2:this.variant==='lanes'?4:1;
      if(this.variant==='belt') {
        this.conveyor(-1.65,0,2.4,2,.55);
        for(let i=0;i<10;i++)this.box(-2.7+i*.18+(this.playing?this.progress*.15:0),2.23,Math.sin(i)*.1,.1,.1,.08,palette.amber);
      }
      if(this.variant==='servo') {
        this.box(-1.45,2,0,2,.1,.5,palette.steel);
        this.box(-2,1.6,0,.4,.4,.5,palette.blue);
      }
      for(let i=0;i<count;i++) {
        const x=multi?Math.cos(TAU*i/count)*.9:(i-(count-1)/2)*.65;
        const z=multi?Math.sin(TAU*i/count)*.9:0;
        this.hopper(x,1.35,z,0.23,0.38);
      }
      this.conveyor(0,0,5.5,0.45,1);
      this.pack(0,0.67,0,this.step>=2?1:0.2,'bag');
      if(this.step===2)this.particles(0,1.35,0,0.6);
    }
    inspectionScene() {
      this.conveyor(0,0,6.7,0.6,1);
      if(this.variant==='check')this.box(0,0.61,0,1.7,0.16,1.1,palette.blue);
      else {
        this.frame(0,0,1.5,1.9,1.45);
        if(this.variant==='xray'){this.box(0,0.82,-.65,1.4,1.1,.1,palette.blue);this.box(0,1.9,0,1.4,.15,1.45,palette.blue);}
      }
      const p=this.playing?this.progress:0.55;
      this.pack(this.step===1?mix(-1.5,.9,p):-2.6,0.82,0,1,'film');
      this.pack(2.5,0.82,0,1,'film');
      this.box(1.25,0.6,1.1,1.1,0.1,1.4,palette.steel);
      this.box(1.25,0.85,-.3,.15,.28,.2,palette.blue);
      if(this.step===2)this.pack(1.25,0.82,mix(0,1.35,p),1,'film');
      this.tube([.7,1.9,-.7],[.7,2.1,-.7],.07,this.step===2?palette.red:palette.green);
    }
    feedingScene() {
      this.hopper(-2.5,.2,0,.7,.55);
      if(this.variant==='bucket') {
        for(const z of [-.3,.3])this.box(-1.7,0,z,.12,2.9,.1,palette.steel);
        this.box(-1.7,0,0,.2,2.9,.45,palette.dark);
        for(let i=0;i<7;i++){const y=(i*.44+this.progress*.45)%2.9;this.box(-1.45,y,0,.4,.2,.35,palette.amber);}
        this.tube([-1.5,2.75,0],[1,2.1,0],.15,palette.steel);
      }else if(this.variant==='vacuum') {
        this.tube([-2.5,.6,0],[-2.5,2.8,0],.09,palette.blue);
        this.tube([-2.5,2.8,0],[1.2,2.8,0],.09,palette.blue);
        this.tube([1.2,2,0],[1.2,2.8,0],.4,palette.steel,12);
        if(this.step===1)for(let i=0;i<8;i++)this.box(mix(-2.5,1.2,(i/8+this.progress)%1),2.8,.01,.06,.06,.06,palette.amber);
      }else this.tube([-2.5,.45,0],[1.2,2.45,0],.2,palette.steel,14);
      this.frame(1.2,0,1.8,1.9,1.5);
      this.hopper(1.2,1.5,0,.6,.6);
      if(this.step>=2)this.particles(1.2,1.55,0,.75);
      this.pack(1.2,.2,0,this.step>1?1:.1,'bag',1.1);
    }
    bulkScene(hot=false) {
      this.frame(0,0,2.3,3.2,2.1);
      this.hopper(0,2.3,0,.65,.65);
      if(hot) {
        this.tube([-2.5,0,0],[-2.5,1.9,0],.6,palette.dark,14);
        this.tube([-2.5,1.7,0],[0,2.6,0],.12,palette.amber);
        this.pack(0,.25,0,this.step>0?1:.1,'film',1.6);
        this.conveyor(2.3,0,2.6,.1,1.1);
      }else {
        this.box(0,.15,0,1.65,1.6,1.45,palette.white);
        this.box(0,.7,.73,.9,.3,.01,palette.blue);
        for(const x of [-.65,.65])for(const z of [-.55,.55])this.tube([x,1.7,z],[x,2.3,z],.04,palette.pack);
        this.pallet(2.6,0,0);
      }
      if(this.step===1)this.particles(0,2.3,0,.6,hot?palette.dark:palette.amber);
    }
    vacuumScene() {
      this.conveyor(0,0,5.6,.5,1.1);
      this.box(0,.7,0,1.7,.65,1.5,palette.steel);
      this.pack(0,1.36,0,1,'film',.8);
      const y=this.step===0?2.1:1.85;
      this.box(0,y,0,1.8,.12,1.6,palette.blue);
      for(const x of [-.75,.75])this.box(x,1.35,0,.035,.5,1.5,palette.light,.25);
      if(this.step===1)for(let i=0;i<5;i++)this.tube([.9,1.45+i*.09,0],[1.5,1.5+i*.08,0],.015,palette.green,5);
      this.pack(2.15,.72,0,1,'film',.8);
    }
    supportScene() {
      this.box(0,1.9,0,3.1,.18,2.4,palette.steel);
      for(const x of [-1.35,1.35])for(const z of [-1,1])this.box(x,0,z,.1,2,.1,palette.dark);
      this.hopper(0,2.1,0,.65,.65);
      for(let i=0;i<7;i++)this.box(-2.3+i*.13,.15+i*.25,.6,.7,.08,.45,palette.steel);
      this.tube([-1.5,2.1,1.15],[1.5,2.1,1.15],.025,palette.amber,6);
      this.tube([-1.5,2.55,1.15],[1.5,2.55,1.15],.025,palette.amber,6);
      for(const x of [-1.5,0,1.5])this.tube([x,2,1.15],[x,2.6,1.15],.025,palette.amber,6);
      this.pack(0,.2,0,1,'bag');
    }
    draw() {
      if (!this.width || !this.ctx) return;
      const ctx=this.ctx,w=this.width,h=this.height;
      ctx.clearRect(0,0,w,h);
      const bg=ctx.createLinearGradient(0,0,0,h);bg.addColorStop(0,'#f1f6f9');bg.addColorStop(1,'#e3edf2');ctx.fillStyle=bg;ctx.fillRect(0,0,w,h);
      this.polys=[];
      this.box(0,-.1,0,8.7,.08,4.1,'#d9e5ec');
      for(let x=-4;x<=4;x++)this.box(x,-.005,0,.009,.004,4,'#c8d7e0');
      for(let z=-2;z<=2;z++)this.box(0,-.005,z,8.6,.004,.009,'#c8d7e0');
      switch(this.family) {
        case 'manual':this.paperScene(true);break;
        case 'paper':this.paperScene();break;
        case 'bag':if(this.variant==='rotary')this.rotaryScene(true);else this.paperScene();break;
        case 'film':this.filmScene(this.variant==='liquid');break;
        case 'pouch':this.rotaryScene();break;
        case 'carton':this.cartonScene();break;
        case 'container':this.cartonScene(true);break;
        case 'case':this.caseScene();break;
        case 'secondary':this.secondaryScene();break;
        case 'robot':this.robotScene();break;
        case 'high':this.robotScene(true);break;
        case 'weigher':this.weigherScene();break;
        case 'inspection':this.inspectionScene();break;
        case 'feed':this.feedingScene();break;
        case 'bulk':this.bulkScene(this.variant==='hot');break;
        case 'vacuum':this.vacuumScene();break;
        case 'platform':this.supportScene();break;
        default:
          this.conveyor(0,0,7,.6,1);
          for(let i=0;i<5;i++)this.pack(-2.8+i*1.3+(this.playing?this.progress*.5:0),.82,0,1,'bag');
      }
      this.polys.sort((a,b)=>a.depth-b.depth).forEach(face=>{
        ctx.beginPath();face.points.forEach((p,i)=>i?ctx.lineTo(p[0],p[1]):ctx.moveTo(p[0],p[1]));ctx.closePath();
        ctx.globalAlpha=face.alpha;ctx.fillStyle=face.color;ctx.fill();ctx.strokeStyle='rgba(51,77,96,.19)';ctx.lineWidth=.55;ctx.stroke();
      });
      ctx.globalAlpha=1;
      const coords=[[-3.1,.1,1.65],[-1.1,.1,1.65],[1,.1,1.65],[3.05,.1,1.65]];
      coords.forEach((point,i)=>{
        const p=this.project(point);ctx.beginPath();ctx.arc(p[0],p[1],11,0,TAU);ctx.fillStyle=i===this.step?'#d98a3c':'#ffffff';ctx.fill();ctx.strokeStyle='#a8bdcc';ctx.lineWidth=1;ctx.stroke();
        ctx.fillStyle=i===this.step?'#fff':'#47647a';ctx.textAlign='center';ctx.textBaseline='middle';ctx.font='600 10px Arial';ctx.fillText(String(i+1),p[0],p[1]+.5);
      });
      this.frames++;
      this.canvas.dataset.view=String(Math.round(this.yaw*180/Math.PI));
      this.canvas.dataset.frames=String(this.frames);
    }
  }

  const init = () => {
    document.querySelectorAll('[data-cxv-root]').forEach(root => {
      if(root.dataset.cxvReady) return;
      root.dataset.cxvReady='true';
      const renderer=new ProcessView(root);
      root.querySelectorAll('[data-cxv-mode]').forEach(button=>button.addEventListener('click',()=>{
        const mode=button.dataset.cxvMode;
        root.querySelectorAll('[data-cxv-mode]').forEach(b=>b.setAttribute('aria-selected',String(b===button)));
        root.querySelectorAll('[data-cxv-panel]').forEach(panel=>{panel.hidden=panel.dataset.cxvPanel!==mode;});
        if(mode!=='model'&&renderer.setPlaying)renderer.setPlaying(false);
        if(mode==='model')root.querySelectorAll('.cxv-video-frame iframe').forEach(frame=>{
          const poster=videoPosters.get(frame);
          if(poster)frame.replaceWith(poster);
        });
        root.dispatchEvent(new CustomEvent('cxv:visible'));
      }));
      root.querySelectorAll('[data-cxv-video]').forEach(button=>button.addEventListener('click',()=>{
        const id=button.dataset.cxvVideo;
        if(!/^[\w-]{11}$/.test(id))return;
        const frame=document.createElement('iframe');
        frame.src='https://www.youtube-nocookie.com/embed/'+id+'?autoplay=1&rel=0';
        frame.title=button.dataset.cxvVideoTitle;
        frame.allow='autoplay; encrypted-media; picture-in-picture; fullscreen';
        frame.allowFullscreen=true;
        frame.referrerPolicy='strict-origin-when-cross-origin';
        videoPosters.set(frame,button);
        button.replaceWith(frame);
      }));
    });
    const reveal=hash=>{
      if(!hash||hash==='#')return;
      let target;try{target=document.querySelector(hash);}catch{return;}
      if(!target)return;
      let parent=target.parentElement;while(parent){if(parent.tagName==='DETAILS')parent.open=true;parent=parent.parentElement;}
    };
    document.addEventListener('click',e=>{const a=e.target.closest('a[href^="#"]');if(a)reveal(a.getAttribute('href'));});
    window.addEventListener('hashchange',()=>reveal(location.hash));
    reveal(location.hash);
  };
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init);else init();
  document.addEventListener('shopify:section:load',init);
})();
